import * as React from 'react';
import { TPAComponentsProvider } from '../components/TPAComponentsConfig';
import { render } from '@testing-library/react';
import { Simulate } from 'react-dom/test-utils';
import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { BaseDriver, ComponentFactory } from '@wix/wix-ui-test-utils';
import { uniDriverImplementation } from './unidriver-config';

export const TPAComponentsWrapper = ({ mobile = false, rtl = false }) => {
  return (Component) => {
    return (
      <TPAComponentsProvider value={{ mobile, rtl }}>
        {Component}
      </TPAComponentsProvider>
    );
  };
};

const getElement = ({ renderResult, dataHook }) => {
  return dataHook
    ? renderResult.container.querySelector(`[data-hook="${dataHook}"]`)
    : renderResult.container.firstChild;
};

/**
 * Creates a `render` function that returns the same object as `react-testing-library`'s render, but
 * with and extra `driver` property.
 *
 * The returned render function arguments:
 * @param [React.Element] jsx a jsx element to render
 * @param [string] dataHook if provided then the driver would be created with the element which is found by the dataHook. If not provided then it assumes that the rendered root element is the component's root element and it will be used for the driver.
 */
export const createRendererWithDriver =
  <Driver extends BaseDriver>(
    driverFactory: (data: ComponentFactory) => Driver,
  ) =>
  (jsx: React.ReactElement, dataHook?: string) => {
    const rendered = render(jsx);

    const element = getElement({ renderResult: rendered, dataHook });
    const driver = driverFactory({
      element,
      wrapper: rendered.container,
      eventTrigger: Simulate,
    });
    return {
      ...rendered,
      driver,
    };
  };

/**
 * Creates a `render` function that returns the same object as `react-testing-library`'s render, but
 * with and extra `driver` property which is a Unidriver.
 *
 * The returned render function arguments:
 * @param [React.Element] jsx a jsx element to render
 * @param [string] dataHook if provided then the driver would be created with the element which is found by the dataHook. If not provided then it assumes that the rendered root element is the component's root element and it will be used for the driver.
 */
export const createRendererWithUniDriver =
  <Driver extends BaseUniDriver>(
    driverFactory: (base: UniDriver, body?: UniDriver) => Driver,
  ) =>
  (jsx: React.ReactElement, dataHook?: string) => {
    const renderResult = render(jsx);

    const element = getElement({ renderResult, dataHook });
    const driver = driverFactory(
      uniDriverImplementation.current(element),
      // @ts-expect-error
      uniDriverImplementation.current(document.body),
    );
    return {
      ...renderResult,
      driver,
    };
  };
